import L from "../constants/baysportLabels";
import { isExtendedNavEnabled } from "./clubMode";

/** Главное меню */
export const CORE_ADMIN_NAV = [
  { id: "overview", to: "/manage/app", end: true, label: L.summary, icon: "overview" },
  { id: "schedule", to: "/manage/app/schedule", label: L.schedule, icon: "schedule" },
  { id: "chat", to: "/manage/app/chat", label: "Чат", icon: "chat" },
  { id: "clients", to: "/manage/app/clients", label: L.clients, icon: "clients" },
  { id: "journal", to: "/manage/app/journal", label: L.journal, icon: "journal" },
];

/** Нижняя навигация PWA */
export const ADMIN_BOTTOM_TABS = [
  ...CORE_ADMIN_NAV,
  { id: "more", to: "/manage/app/more", label: "Ещё", icon: "settings" },
];

const CORE_BOTTOM_TAB_IDS = new Set(CORE_ADMIN_NAV.map((item) => item.id));

/** Раздел «Ещё» */
export const MORE_ADMIN_NAV = [
  { id: "life", to: "/manage/app/life", label: "Клуб", icon: "life" },
  { id: "groups", to: "/manage/app/groups", label: L.groups, icon: "groups" },
  {
    id: "memberships",
    to: "/manage/app/memberships",
    label: L.memberships,
    icon: "memberships",
  },
  { id: "finance", to: "/manage/app/finance", label: "Финансы", icon: "finance" },
  {
    id: "analytics",
    to: "/manage/app/analytics",
    label: "Аналитика",
    icon: "overview",
  },
  { id: "venues", to: "/manage/app/venues", label: L.venues, icon: "venues" },
  {
    id: "directions",
    to: "/manage/app/directions",
    label: L.directions,
    icon: "directions",
  },
  { id: "polls", to: "/manage/app/polls", label: "Опросы", icon: "polls" },
  { id: "team", to: "/manage/app/team", label: L.team, icon: "trainers" },
];

export const ADMIN_SETTINGS_NAV = {
  id: "settings",
  to: "/manage/app/settings",
  label: "Настройки",
  icon: "settings",
};

export function getClubFeatures(club, tenant) {
  const teamCount = Number(club?.team_members_count ?? 0);
  const isSolo = club?.is_solo_club ?? teamCount === 0;
  const isClubPlan =
    tenant?.plan_tier === "club" || club?.plan_tier === "club";
  const extended = isExtendedNavEnabled(club, tenant);
  const directionsCount = Number(club?.directions_count ?? 0);
  const showDirectionsInMain =
    isClubPlan || directionsCount > 1;

  return {
    teamCount,
    isSolo,
    isClubPlan,
    extended,
    directionsCount,
    showTeam: true,
    showDirectionsNav: showDirectionsInMain,
    showVenuesNav: true,
    /** Временно скрыть «Опросы» в меню PWA и десктопа */
    showPollsNav: true,
  };
}

/** Доп. пункты в «Ещё» (зарезервировано под тарифные фичи) */
export function getMoreAdminNavExtras() {
  return [];
}

export function buildAdminSidebarNav(club, tenant) {
  const f = getClubFeatures(club, tenant);
  const more = [...MORE_ADMIN_NAV, ...getMoreAdminNavExtras()].filter(
    (item) => f.showPollsNav || item.id !== "polls",
  );
  return {
    main: [...CORE_ADMIN_NAV],
    more,
    footer: [ADMIN_SETTINGS_NAV],
    extended: f.extended,
  };
}

export function resolveAdminBottomTab(pathname) {
  const tab = resolveAdminTabId(pathname);
  if (CORE_BOTTOM_TAB_IDS.has(tab)) return tab;
  return "more";
}

export function resolveAdminPageTitle(tabId, club, tenant) {
  if (tabId === "overview") return null;
  if (tabId === "more") return "Ещё";
  if (tabId === "notifications") return "Уведомления";
  if (tabId === "profile") return "Профиль";
  const nav = buildAdminSidebarNav(club, tenant);
  const all = [...nav.main, ...nav.more, ...nav.footer];
  const item = all.find((entry) => entry.id === tabId);
  if (item?.label) return item.label;
  if (tabId === "trainers") return L.trainers;
  if (tabId === "appointments") return L.schedule;
  return L.adminTitle;
}

export function resolveAdminTabId(pathname) {
  const path = pathname.replace(/\/$/, "") || "/manage/app";
  if (path === "/manage/app/more") return "more";
  const ordered = [
    { id: "chat", path: "/manage/app/chat" },
    { id: "clients", path: "/manage/app/clients" },
    { id: "memberships", path: "/manage/app/memberships" },
    { id: "team", path: "/manage/app/team" },
    { id: "trainers", path: "/manage/app/trainers" },
    { id: "life", path: "/manage/app/life" },
    { id: "notifications", path: "/manage/app/notifications" },
    { id: "polls", path: "/manage/app/polls" },
    { id: "appointments", path: "/manage/app/appointments" },
    { id: "directions", path: "/manage/app/directions" },
    { id: "settings", path: "/manage/app/settings" },
    { id: "profile", path: "/manage/app/profile" },
    { id: "analytics", path: "/manage/app/analytics" },
    { id: "schedule", path: "/manage/app/schedule" },
    { id: "finance", path: "/manage/app/finance" },
    { id: "journal", path: "/manage/app/journal" },
    { id: "groups", path: "/manage/app/groups" },
    { id: "venues", path: "/manage/app/venues" },
    { id: "overview", path: "/manage/app" },
  ];
  const match = ordered.find(
    (t) => path === t.path || (t.path !== "/manage/app" && path.startsWith(`${t.path}/`)),
  );
  return match?.id || "overview";
}

export function redirectForHiddenAdminRoute(pathname, club, tenant, search = "") {
  const f = getClubFeatures(club, tenant);
  const path = pathname.replace(/\/$/, "");

  if (path === "/manage/app/trainers" || path === "/manage/app/trainers/") {
    return "/manage/app/team";
  }
  if (path === "/manage/app/trainers/new") {
    return "/manage/app/team/invite";
  }
  if (
    path === "/manage/app/branches" ||
    path.startsWith("/manage/app/branches/")
  ) {
    return "/manage/app/venues";
  }
  if (
    path === "/manage/app/services" ||
    path.startsWith("/manage/app/services/")
  ) {
    return "/manage/app/directions";
  }
  if (
    path === "/manage/app/appointments" ||
    path.startsWith("/manage/app/appointments/") ||
    path === "/manage/app/calendar" ||
    path.startsWith("/manage/app/calendar/") ||
    path === "/manage/app/waitlist" ||
    path.startsWith("/manage/app/waitlist/")
  ) {
    return "/manage/app/schedule";
  }
  if (
    (path === "/manage/app/polls" || path.startsWith("/manage/app/polls/")) &&
    !f.showPollsNav
  ) {
    return "/manage/app/more";
  }
  return null;
}

/** Ссылка на страницу журнала посещаемости */
export function journalUrl({ date, sessionId, groupId } = {}) {
  const params = new URLSearchParams();
  if (date) params.set("date", date);
  if (sessionId) params.set("session", sessionId);
  if (groupId) params.set("group", groupId);
  const qs = params.toString();
  return qs ? `/manage/app/journal?${qs}` : "/manage/app/journal";
}

/** Ссылка на календарь занятий (вкладка «Календарь») */
export function scheduleUrl({
  date,
  sessionId,
  groupId,
  trainerId,
  view = "month",
} = {}) {
  const params = new URLSearchParams();
  params.set("view", view);
  if (date) params.set("date", date);
  if (sessionId) params.set("session", sessionId);
  if (groupId) params.set("group", groupId);
  if (trainerId) params.set("trainer", trainerId);
  return `/manage/app/schedule?${params.toString()}`;
}
